import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import Text from 'components/shared/DesignSystem/SlabTransferPortal/Text';
import { updateDocument } from 'actions/documents';
import toast from 'helpers/toast';

const ListItem = styled.span`
  width: 100%;
  height: 100%;
  padding: 4px 0px;
  display: flex;
  align-items: center;
  overflow: visible;
  vertical-align: middle;
  margin-left: -10px;
  cursor: pointer;
`;

const Icon = styled.div`
  margin-inline-end: var(--cc_size_spacing_m);
  margin-inline-start: var(--cc_size_spacing_s);
  height: 40px;
  width: 40px;
  border-radius: 50%;
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 10px 13px;

  background: ${({ isSelected }) => {
    if (isSelected) return 'var(--cc_color_brand_primary)';
    return 'var(--cc_color_button_background_subtle)';
  }};

  color: ${({ isSelected }) => {
    if (isSelected) return 'var(--cc_color_button_text_primary)';
    return 'var(--cc_color_text_subtle)';
  }};

  i {
    transform: scale(1.5) !important;
    margin: 0 !important;
  }
`;

export const DOCUMENT_ACCESS_PERMS = {
  OPEN: 'OPEN',
  LIMITED: 'LIMITED',
};

export const accessManagementData = [
  {
    value: DOCUMENT_ACCESS_PERMS.OPEN,
    iconName: 'account-multiple',
    label: (
      <FormattedMessage
        id="Documents.document.manageAccess.openPermissionLabel"
        defaultMessage="Everyone"
        description="Title label for card that gives everyone access to document"
      />
    ),
    secondary: (
      <FormattedMessage
        id="Documents.document.manageAccess.openPermissionText"
        defaultMessage="Everyone with access to this project can view"
        description="Subtitle label for card that gives everyone access to document"
      />
    ),
  },
  {
    value: DOCUMENT_ACCESS_PERMS.LIMITED,
    iconName: 'lock',
    label: (
      <FormattedMessage
        id="Documents.document.manageAccess.restrictedPermissionLabel"
        defaultMessage="Restricted"
        description="Title label for card that limits document access"
      />
    ),
    secondary: (
      <FormattedMessage
        id="Documents.document.manageAccess.restrictedPermissionText"
        defaultMessage="Only the uploader, admins and managers can view"
        description="Subtitle label for card that limits document access"
      />
    ),
  },
];

export const shouldLimitAccess = (selectedPermission) => {
  return selectedPermission === DOCUMENT_ACCESS_PERMS.LIMITED;
};

export const saveDocumentAccess = async ({
  documentId,
  dispatch,
  selectedPermission,
  onSuccess,
  onError,
}) => {
  await dispatch(
    updateDocument(documentId, {
      access_limited: shouldLimitAccess(selectedPermission),
    }),
  )
    .then(() => {
      toast.success(
        <FormattedMessage
          id="Documents.document.manageAccess.success"
          defaultMessage="Updated access settings"
          description="Success toast for updating document access"
        />,
      );
      onSuccess({
        documentId,
        documentAccess: shouldLimitAccess(selectedPermission),
      });
    })
    .catch(() => {
      const errMsg = (
        <FormattedMessage
          id="Documents.document.manageAccess.failure"
          defaultMessage="There was an error updating your access settings."
          description="Failure toast for updating document access"
        />
      );
      onError(errMsg);
    });
};

const AccessManagementListItem = ({ item, isSelected }) => {
  return (
    <ListItem isSelected={isSelected}>
      <Icon isSelected={isSelected}>
        <i className={`mdi mdi-${item.iconName}`} />
      </Icon>
      <div>
        <Text size="s" color="subtle" family="heading" weight="bold">
          {item.label}
        </Text>
        <Text size="xs" color="subtle" family="system">
          {item.secondary}
        </Text>
      </div>
    </ListItem>
  );
};

AccessManagementListItem.propTypes = {
  item: PropTypes.object,
  isSelected: PropTypes.bool,
};

export default AccessManagementListItem;
